<div class="w-full relative min-w-0">
  <!-- Input Field Container -->
  <div class="relative w-full">
    <!-- Multiple Select: Show chips above input -->
    <div *ngIf="multiple && selectedValues.length > 0" class="mb-2 flex flex-wrap gap-1">
      <span *ngFor="let val of selectedValues" class="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-[10px] rounded">
        {{ getOptionLabel(val) }}
        <button type="button" (click)="removeSelected(val, $event)" (mousedown)="$event.preventDefault()" class="hover:text-red-500">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </span>
    </div>

    <!-- Input Field -->
    <div class="relative">
      <input
        #inputElement
        type="text"
        [id]="id"
        [name]="name"
        [value]="displayValue"
        (input)="onInputChange($event)"
        (focus)="onInputFocus()"
        (blur)="onInputBlur($event)"
        (keydown)="onKeyDown($event)"
        (keyup)="onKeyUp($event)"
        [placeholder]="placeholder || ''"
        [disabled]="disabled"
        [readonly]="false"
        [class.border-red-500]="hasError"
        [class.pr-11]="icon"
        class="w-full px-3 py-2.5 text-[14px] border border-primary rounded-md text-primary bg-bg focus:outline-none transition-colors disabled:bg-surface disabled:cursor-not-allowed disabled:opacity-60 font-semibold h-[40px]"
        autocomplete="off">

      <!-- Label: above when placeholder exists and empty, so Plan label doesn't overlap "Search plan or insurance name" -->
      <label *ngIf="label" [for]="id"
        [class.floating-label-active]="isFocused || hasValue || displayValue"
        [class.text-red-500]="required" [class.text-muted]="!required"
        class="floating-label absolute left-3 text-[13px] font-medium pointer-events-none transition-all duration-200">
        {{ label }}
        <span *ngIf="required" class="text-red-500 ml-0.5">*</span>
      </label>

      <!-- Clear button for single select -->
      <button *ngIf="!multiple && hasValue && !disabled && !icon" 
        type="button"
        (click)="clearSelection()"
        (mousedown)="$event.preventDefault()"
        class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500 transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <!-- Icon (shown when icon is provided, takes precedence over clear button) -->
      <button
        *ngIf="icon"
        type="button"
        [disabled]="iconDisabled"
        class="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-md p-1.5 min-w-[2rem] min-h-[2rem] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50 enabled:cursor-pointer enabled:hover:bg-primary/10 enabled:active:bg-primary/15"
        [attr.title]="iconTitle || null"
        [attr.aria-label]="iconTitle || 'Edit'"
        (click)="onIconClick()"
        (mousedown)="$event.preventDefault()">
        <i [class]="icon + ' text-[15px] leading-none pointer-events-none ' + iconColor"></i>
      </button>
    </div>
  </div>

      <!-- Dropdown Options Overlay -->
      <div *ngIf="isOpen" [ngStyle]="dropdownStyle"
        #dropdownContainer
        class="animate-in fade-in duration-200 dropdown-menu"
        [class.slide-in-from-bottom-2]="openAbove" [class.slide-in-from-top-2]="!openAbove"
        (mousedown)="$event.preventDefault()"
        (scroll)="onDropdownScroll($event)">
        
        <!-- Full loading only when nothing to show yet (server results replace list as soon as they arrive) -->
        <div *ngIf="isLoading && filteredOptions.length === 0" class="px-4 py-3 text-center text-slate-500 text-sm">
          <div class="flex items-center justify-center gap-2">
            <svg class="animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Searching...</span>
          </div>
        </div>

        <!-- Options List -->
        <div>
          <div *ngIf="isLoading && filteredOptions.length > 0 && !enablePagination" class="px-3 py-1.5 text-center text-slate-500 text-xs border-b border-slate-200 bg-slate-50 flex items-center justify-center gap-2">
            <svg class="animate-spin h-3.5 w-3.5 text-primary shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Updating results…</span>
          </div>
          <div *ngIf="!isLoading && filteredOptions.length === 0" class="px-4 py-3 text-center text-slate-500 text-sm">
            No results found
          </div>
          <div *ngFor="let option of filteredOptions; trackBy: trackByOption" 
            (mousedown)="selectOption(option, $event)"
            class="dropdown-option group cursor-pointer text-[14px]"
            [class.isSelected]="isSelected(option)"
            [class.has-right-label]="option.rightLabel"
            [title]="option.tooltip || option.label">
            <span class="option-label transition-colors" 
              [class.text-secondary]="isSelected(option)"
              [class.font-semibold]="isSelected(option)" 
              [class.text-slate-700]="!isSelected(option)">
              {{ option.label }}
            </span>
            <span *ngIf="option.rightLabel" 
              class="option-right-label text-slate-500 text-[13px] ml-auto pl-4"
              [class.text-secondary]="isSelected(option)">
              {{ option.rightLabel }}
            </span>
            <div *ngIf="isSelected(option)" class="text-secondary ml-2 flex-shrink-0">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <!-- Loading more indicator at bottom (only show when loading more, not initial load) -->
          <div *ngIf="enablePagination && isLoading && filteredOptions.length > 0" class="px-4 py-2 text-center text-slate-500 text-sm border-t border-slate-200">
            <div class="flex items-center justify-center gap-2">
              <svg class="animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading more...</span>
            </div>
          </div>
        </div>
  </div>
  <!-- Error messages should be handled by app-form-error-message component in the parent template -->
</div>
